import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Slide,
} from "@material-ui/core";
import { Check } from "@material-ui/icons";
import axios from "axios";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import SweetAlert from "react-bootstrap-sweetalert";

import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";

import stylesAlert from "assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js";
import stylesModal from "assets/jss/material-dashboard-pro-react/views/notificationsStyle.js";
import stylesCheckbox from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";
import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";
import * as Constants from "constants.js";

axios.defaults.withCredentials = true;
const { REACT_APP_SERVER_URL } = process.env;

// ========== CONSTANTS ==========
const ALERT_OVERRIDES = {
  marginTop: "-100px",
  marginRight: "0px",
  marginBottom: "0px",
  marginLeft: "0px",
};

const ROLE_TYPES = {
  MD: { value: "2", code: "md", label: "MD/DO" },
  PHD: { value: "3", code: "phd", label: "PhD" },
  RN: { value: "4", code: "rn", label: "RN" },
  RCP: { value: "5", code: "rcp", label: "RCP" },
  STUDENT: { value: "6", code: "student", label: "Student" },
};

const REG_TYPES = {
  IN_PERSON: { value: "2", code: "in_person", label: "In Person" },
  VIRTUAL: { value: "3", code: "virtual", label: "Virtual" },
};

const AFFILIATION_TYPES = {
  PALICC: { value: 1, code: "palicc", label: "Affiliated with PALICC-2" },
  VENT_LIB: {
    value: 2,
    code: "vent_lib",
    label: "Affiliated with Vent Liberation",
  },
};

// ========== STYLES ==========
const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
    textAlign: "left",
  },
  size: {
    fontSize: "large",
    color: "black",
    opacity: 1,
    textAlign: "left",
  },
  list: {
    display: "inline",
    padding: "5px",
  },
  heading: {
    fontWeight: "bold",
    textAlign: "center",
  },
};

const useStyles = makeStyles({
  ...stylesAlert,
  ...stylesModal,
  ...stylesCheckbox,
  ...styles,
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// ========== HELPER FUNCTIONS ==========
const mapRoleTypeToCode = (roleTypeNumeric) => {
  const mapping = {
    2: "md",
    3: "phd",
    4: "rn",
    5: "rcp",
    6: "student",
  };
  return mapping[roleTypeNumeric] || "student";
};

const mapRegTypeToCode = (regTypeNumeric) => {
  return regTypeNumeric === "2" ? "in_person" : "virtual";
};

const mapAffiliationToCode = (affiliation) => {
  return affiliation
    .map((item) => {
      if (item === 1) return "palicc";
      if (item === 2) return "vent_lib";
      return null;
    })
    .filter(Boolean);
};

// ========== MAIN COMPONENT ==========
export default function ConferenceRegistration(props) {
  const classes = useStyles();
  const history = useHistory();
  const { userInfo, registrations, reloadRegistrationsCallBack } = props;
  const access = userInfo?.access || 1;

  // State
  const [alert, setAlert] = React.useState(null);
  const [registrationModal, setRegistrationModal] = React.useState(false);
  const [conference, setConference] = React.useState("");
  const [name, setName] = React.useState(props.userInfo?.name || "");
  const [institution, setInstitution] = React.useState("");
  const [agendaTopic, setAgendaTopic] = React.useState("");
  const [agendaTime, setAgendaTime] = React.useState("");
  const [roleTypeNumeric, setRoleTypeNumeric] = React.useState("1");
  const [regTypeNumeric, setRegTypeNumeric] = React.useState("1");
  const [needTimeNumeric, setNeedTimeNumeric] = React.useState("1");
  const [affiliation, setAffiliation] = React.useState([]);
  const [agreement, setAgreement] = React.useState([]);

  const currentPreventMeeting = Constants.CURRENT_MEETING;
  const currentPreventMeetingTitle = Constants.CURRENT_MEETING_TITLE;
  const currentPreventMeetingDate = Constants.CURRENT_MEETING_DATE;
  const currentPreventMeetingLocation = Constants.CURRENT_MEETING_LOCATION;

  // ========== NAVIGATION ==========
  const goToMyConferences = () => {
    history.push("/portal/my_conferences");
  };

  // ========== HANDLERS ==========
  const hideAlert = () => setAlert(null);

  const handleSetRegistrationModal = (conferenceName) => {
    setConference(conferenceName);
    setRegistrationModal(true);
  };

  const toggleArrayValue = (array, value, setArray) => {
    const currentIndex = array.indexOf(value);
    const newArray = [...array];
    if (currentIndex === -1) {
      newArray.push(value);
    } else {
      newArray.splice(currentIndex, 1);
    }
    setArray(newArray);
  };

  const handleAffiliation = (value) => {
    toggleArrayValue(affiliation, value, setAffiliation);
  };

  const handleAgreement = (value) => {
    toggleArrayValue(agreement, value, setAgreement);
  };

  // ========== VALIDATION ==========
  const isFormValid = () => {
    const hasRequiredFields =
      name?.length > 0 &&
      institution?.length > 0 &&
      roleTypeNumeric > "1" &&
      regTypeNumeric > "1" &&
      agreement.length > 0;

    const hasAgendaIfNeeded =
      needTimeNumeric !== "2" ||
      (agendaTopic.length > 0 && agendaTime.length > 0);

    return hasRequiredFields && hasAgendaIfNeeded;
  };

  // ========== REGISTRATION LOGIC ==========
  const buildRegistrationPayload = () => {
    const email = userInfo?.email;
    return {
      conference_name: conference,
      name,
      institution,
      email,
      role: mapRoleTypeToCode(roleTypeNumeric),
      registration_type: mapRegTypeToCode(regTypeNumeric),
      affiliation: mapAffiliationToCode(affiliation),
      agreement: agreement[0] === 1,
      need_time: needTimeNumeric === "2",
      agenda_topic: agendaTopic,
      agenda_time: agendaTime,
    };
  };

  const adjustPayloadForConference = (payload) => {
    if (conference === "palicc_prevent_symposium_2022") {
      payload.need_time = false;
      payload.agenda_topic = "";
      payload.agenda_time = "";
    }
    if (conference === currentPreventMeeting) {
      payload.affiliation = [];
    }
    return payload;
  };

  const getRegistrations = () => {
    if (reloadRegistrationsCallBack) {
      reloadRegistrationsCallBack();
    }
  };

  const handleSubmitAddRegistration = async (e) => {
    e.preventDefault();
    let payload = buildRegistrationPayload();
    payload = adjustPayloadForConference(payload);

    try {
      const response = await axios.post(
        `${REACT_APP_SERVER_URL}/registrations`,
        payload,
        { withCredentials: true },
      );

      if (response.status === 200) {
        showSuccessAddAlert();
        getRegistrations();
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (
        errorData?.messages?.databaseError ===
        "The email has already been taken."
      ) {
        showConfirmUpdateAlert(payload);
      } else {
        showFailureAlert(errorData?.messages?.databaseError);
      }
    }
  };

  const handleSubmitUpdateRegistration = async (payload) => {
    const email = userInfo?.email;
    const registration = registrations?.find(
      (reg) =>
        reg.email === email && reg.conference_name === payload.conference_name,
    );

    if (!registration) {
      showFailureAlert("Registration not found");
      return;
    }

    try {
      const response = await axios.put(
        `${REACT_APP_SERVER_URL}/registrations/${registration.id}`,
        payload,
        { withCredentials: true },
      );

      if (response.status === 200) {
        showSuccessUpdateAlert();
        getRegistrations();
      }
    } catch (error) {
      showFailureAlert(error.response?.data?.messages?.databaseError);
    }
  };

  // ========== ALERT FUNCTIONS ==========
  const showSuccessAddAlert = () => {
    setAlert(
      <SweetAlert
        success
        style={ALERT_OVERRIDES}
        title="Success!"
        onConfirm={() => {
          hideAlert();
          setRegistrationModal(false);
          goToMyConferences();
        }}
        confirmBtnCssClass={`${classes.button} ${classes.success}`}
      >
        You have been registered! You will be redirected to your conferences
        page!
      </SweetAlert>,
    );
  };

  const showSuccessUpdateAlert = () => {
    setAlert(
      <SweetAlert
        success
        style={ALERT_OVERRIDES}
        title="Success!"
        onConfirm={() => {
          hideAlert();
          setRegistrationModal(false);
          goToMyConferences();
        }}
        confirmBtnCssClass={`${classes.button} ${classes.success}`}
      >
        Registration has been updated! You will be redirected to your
        conferences page!
      </SweetAlert>,
    );
  };

  const showFailureAlert = (errorMessage) => {
    setAlert(
      <SweetAlert
        danger
        style={ALERT_OVERRIDES}
        title={errorMessage || "Something Went Wrong!"}
        onConfirm={hideAlert}
        confirmBtnCssClass={`${classes.button} ${classes.danger}`}
      >
        {name} has NOT been updated!
      </SweetAlert>,
    );
  };

  const showConfirmUpdateAlert = (payload) => {
    setAlert(
      <SweetAlert
        warning
        style={ALERT_OVERRIDES}
        title="You are already registered for this conference. Would you like to update your registration with these values?"
        onConfirm={() => handleSubmitUpdateRegistration(payload)}
        onCancel={() => {
          setAlert(
            <SweetAlert
              danger
              style={ALERT_OVERRIDES}
              title="Cancelled"
              onConfirm={hideAlert}
              confirmBtnCssClass={`${classes.button} ${classes.success}`}
            >
              Registration not Updated!
            </SweetAlert>,
          );
        }}
        confirmBtnCssClass={`${classes.button} ${classes.success}`}
        cancelBtnCssClass={`${classes.button} ${classes.danger}`}
        confirmBtnText="Yes, Update Registration"
        cancelBtnText="Cancel"
        showCancel
      >
        Registration will be Updated
      </SweetAlert>,
    );
  };

  // ========== RENDER ==========
  return (
    <div>
      {alert}

      <RegistrationModal
        open={registrationModal}
        onClose={() => setRegistrationModal(false)}
        conference={conference}
        currentPreventMeeting={currentPreventMeeting}
        classes={classes}
        name={name}
        setName={setName}
        institution={institution}
        setInstitution={setInstitution}
        roleTypeNumeric={roleTypeNumeric}
        setRoleTypeNumeric={setRoleTypeNumeric}
        regTypeNumeric={regTypeNumeric}
        setRegTypeNumeric={setRegTypeNumeric}
        needTimeNumeric={needTimeNumeric}
        setNeedTimeNumeric={setNeedTimeNumeric}
        agendaTopic={agendaTopic}
        setAgendaTopic={setAgendaTopic}
        agendaTime={agendaTime}
        setAgendaTime={setAgendaTime}
        affiliation={affiliation}
        handleAffiliation={handleAffiliation}
        agreement={agreement}
        handleAgreement={handleAgreement}
        onSubmit={handleSubmitAddRegistration}
        isFormValid={isFormValid}
      />

      <ConferenceCard
        title="PREVENT Meeting"
        subtitle={currentPreventMeetingTitle}
        date={currentPreventMeetingDate}
        location={currentPreventMeetingLocation}
        access={access}
        onRegister={() => handleSetRegistrationModal(currentPreventMeeting)}
        classes={classes}
      />
    </div>
  );
}

// ========== SUB-COMPONENTS ==========
function RegistrationModal({
  open,
  onClose,
  conference,
  currentPreventMeeting,
  classes,
  name,
  setName,
  institution,
  setInstitution,
  roleTypeNumeric,
  setRoleTypeNumeric,
  regTypeNumeric,
  setRegTypeNumeric,
  needTimeNumeric,
  setNeedTimeNumeric,
  agendaTopic,
  setAgendaTopic,
  agendaTime,
  setAgendaTime,
  affiliation,
  handleAffiliation,
  handleAgreement,
  onSubmit,
  isFormValid,
}) {
  const shouldShowAffiliation = conference !== currentPreventMeeting;
  const shouldShowAgendaFields = conference === currentPreventMeeting;

  return (
    <Dialog
      classes={{ paper: classes.modal }}
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className={classes.modalHeader}>
        <h4 className={classes.modalTitle}>Register for Conference</h4>
      </DialogTitle>

      <DialogContent className={classes.modalBody}>
        <form onSubmit={onSubmit}>
          <GridContainer>
            <GridItem xs={12}>
              <CustomInput
                labelText="Name *"
                formControlProps={{ fullWidth: true }}
                inputProps={{
                  value: name,
                  onChange: (e) => setName(e.target.value),
                }}
              />
            </GridItem>

            <GridItem xs={12}>
              <CustomInput
                labelText="Institution *"
                formControlProps={{ fullWidth: true }}
                inputProps={{
                  value: institution,
                  onChange: (e) => setInstitution(e.target.value),
                }}
              />
            </GridItem>

            <GridItem xs={12}>
              <FormControl fullWidth className={classes.selectFormControl}>
                <InputLabel className={classes.selectLabel}>
                  Select Role Type *
                </InputLabel>
                <Select
                  MenuProps={{ className: classes.selectMenu }}
                  classes={{ select: classes.select }}
                  value={roleTypeNumeric}
                  onChange={(e) => setRoleTypeNumeric(e.target.value)}
                >
                  <MenuItem
                    disabled
                    value="1"
                    classes={{ root: classes.selectMenuItem }}
                  >
                    Select Type
                  </MenuItem>
                  {Object.values(ROLE_TYPES).map((role) => (
                    <MenuItem
                      key={role.value}
                      value={role.value}
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                    >
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem xs={12}>
              <FormControl fullWidth className={classes.selectFormControl}>
                <InputLabel className={classes.selectLabel}>
                  Select Registration Type *
                </InputLabel>
                <Select
                  MenuProps={{ className: classes.selectMenu }}
                  classes={{ select: classes.select }}
                  value={regTypeNumeric}
                  onChange={(e) => setRegTypeNumeric(e.target.value)}
                >
                  <MenuItem
                    disabled
                    value="1"
                    classes={{ root: classes.selectMenuItem }}
                  >
                    Select Type
                  </MenuItem>
                  {Object.values(REG_TYPES).map((type) => (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                    >
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            {shouldShowAgendaFields && (
              <AgendaFields
                classes={classes}
                needTimeNumeric={needTimeNumeric}
                setNeedTimeNumeric={setNeedTimeNumeric}
                agendaTopic={agendaTopic}
                setAgendaTopic={setAgendaTopic}
                agendaTime={agendaTime}
                setAgendaTime={setAgendaTime}
              />
            )}

            {shouldShowAffiliation && (
              <AffiliationFields
                classes={classes}
                affiliation={affiliation}
                handleAffiliation={handleAffiliation}
              />
            )}

            <GridItem xs={12}>
              <FormControlLabel
                classes={{
                  root: classes.checkboxLabelControl,
                  label: classes.checkboxLabel,
                }}
                control={
                  <Checkbox
                    tabIndex={-1}
                    onClick={() => handleAgreement(1)}
                    checkedIcon={<Check className={classes.checkedIcon} />}
                    icon={<Check className={classes.uncheckedIcon} />}
                    classes={{
                      checked: classes.checked,
                      root: classes.checkRoot,
                    }}
                  />
                }
                label={
                  <span>
                    I agree to the{" "}
                    <a
                      href="/portal/agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      confidentiality agreement
                    </a>
                    .
                  </span>
                }
              />
            </GridItem>

            <GridItem xs={12}>
              <Button
                color="info"
                className={classes.updateProfileButton}
                type="submit"
                disabled={!isFormValid()}
              >
                Register!
              </Button>
            </GridItem>
          </GridContainer>
        </form>
      </DialogContent>

      <DialogActions
        className={`${classes.modalFooter} ${classes.modalFooterCenter}`}
      >
        <Button onClick={onClose} color="rose" round>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AgendaFields({
  classes,
  needTimeNumeric,
  setNeedTimeNumeric,
  agendaTopic,
  setAgendaTopic,
  agendaTime,
  setAgendaTime,
}) {
  return (
    <>
      <GridItem xs={12}>
        <FormControl fullWidth className={classes.selectFormControl}>
          <InputLabel className={classes.selectLabel}>
            Do you want time on the agenda?
          </InputLabel>
          <Select
            MenuProps={{ className: classes.selectMenu }}
            classes={{ select: classes.select }}
            value={needTimeNumeric}
            onChange={(e) => setNeedTimeNumeric(e.target.value)}
          >
            <MenuItem
              disabled
              value="1"
              classes={{ root: classes.selectMenuItem }}
            >
              Select
            </MenuItem>
            <MenuItem
              value="2"
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              Yes
            </MenuItem>
            <MenuItem
              value="3"
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              No
            </MenuItem>
          </Select>
        </FormControl>
      </GridItem>

      {needTimeNumeric === "2" && (
        <>
          <GridItem xs={12}>
            <CustomInput
              labelText="Agenda Topic *"
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: agendaTopic,
                onChange: (e) => setAgendaTopic(e.target.value),
              }}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomInput
              labelText="Time Requested (in minutes) *"
              formControlProps={{ fullWidth: true }}
              inputProps={{
                type: "number",
                value: agendaTime,
                onChange: (e) => setAgendaTime(e.target.value),
              }}
            />
          </GridItem>
        </>
      )}
    </>
  );
}

function AffiliationFields({ classes, affiliation, handleAffiliation }) {
  return (
    <GridItem xs={12}>
      <div className={classes.checkboxAndRadio}>
        {Object.values(AFFILIATION_TYPES).map((type) => (
          <FormControlLabel
            key={type.value}
            control={
              <Checkbox
                tabIndex={-1}
                onClick={() => handleAffiliation(type.value)}
                checked={affiliation.includes(type.value)}
                checkedIcon={<Check className={classes.checkedIcon} />}
                icon={<Check className={classes.uncheckedIcon} />}
                classes={{
                  checked: classes.checked,
                  root: classes.checkRoot,
                }}
              />
            }
            classes={{
              label: classes.label,
              root: classes.labelRoot,
            }}
            label={type.label}
          />
        ))}
      </div>
    </GridItem>
  );
}

function ConferenceCard({
  title,
  subtitle,
  date,
  location,
  access,
  onRegister,
  classes,
}) {
  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card profile>
          <CardBody profile>
            <div className={classes.size}>
              <h3 className={classes.heading}>
                <b>{title}</b>
              </h3>
              <h5 className={classes.heading}>{subtitle}</h5>
              <br />
              <h4 className={classes.heading}>Date/Time</h4>
              <h5 className={classes.heading}>{date}</h5>
              <h4 className={classes.heading}>Location</h4>
              <h5 className={classes.heading}>{location}</h5>
              <br />
              <Button
                style={{ fontSize: "large" }}
                color="info"
                className={classes.updateProfileButton}
                disabled={access <= 2}
                onClick={onRegister}
              >
                {access <= 2 ? "Must be Logged in to Register!" : "Register!"}
              </Button>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

// ========== PROP TYPES ==========
ConferenceRegistration.propTypes = {
  userInfo: PropTypes.object,
  registrations: PropTypes.array,
  users: PropTypes.array,
  reloadRegistrationsCallBack: PropTypes.func,
  access: PropTypes.number,
};

RegistrationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  conference: PropTypes.string.isRequired,
  currentPreventMeeting: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
  institution: PropTypes.string.isRequired,
  setInstitution: PropTypes.func.isRequired,
  roleTypeNumeric: PropTypes.string.isRequired,
  setRoleTypeNumeric: PropTypes.func.isRequired,
  regTypeNumeric: PropTypes.string.isRequired,
  setRegTypeNumeric: PropTypes.func.isRequired,
  needTimeNumeric: PropTypes.string.isRequired,
  setNeedTimeNumeric: PropTypes.func.isRequired,
  agendaTopic: PropTypes.string.isRequired,
  setAgendaTopic: PropTypes.func.isRequired,
  agendaTime: PropTypes.string.isRequired,
  setAgendaTime: PropTypes.func.isRequired,
  affiliation: PropTypes.array.isRequired,
  handleAffiliation: PropTypes.func.isRequired,
  agreement: PropTypes.array.isRequired,
  handleAgreement: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isFormValid: PropTypes.func.isRequired,
};

AgendaFields.propTypes = {
  classes: PropTypes.object.isRequired,
  needTimeNumeric: PropTypes.string.isRequired,
  setNeedTimeNumeric: PropTypes.func.isRequired,
  agendaTopic: PropTypes.string.isRequired,
  setAgendaTopic: PropTypes.func.isRequired,
  agendaTime: PropTypes.string.isRequired,
  setAgendaTime: PropTypes.func.isRequired,
};

AffiliationFields.propTypes = {
  classes: PropTypes.object.isRequired,
  affiliation: PropTypes.array.isRequired,
  handleAffiliation: PropTypes.func.isRequired,
};

ConferenceCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  access: PropTypes.number.isRequired,
  onRegister: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

